import type { ComputedRef } from 'vue';

type MaybeNullableColor = string | null | undefined;

/**
 * Resolves a primary-button colour for enabled vs disabled UI state.
 * When disabled, uses the disabled-specific colour if set, otherwise falls back to the enabled colour.
 * Trailing `?? undefined` normalises `null` (e.g. both variants null) to `undefined` for a stable `string | undefined` type.
 */
export const resolveButtonStateColor = (
  isDisabled: boolean,
  disabledVariant: MaybeNullableColor,
  enabledVariant: MaybeNullableColor
): string | undefined => {
  if (isDisabled) {
    return disabledVariant ?? enabledVariant ?? undefined;
  }
  return enabledVariant ?? undefined;
};

/**
 * Creates a style object with backgroundColor if color is provided
 * @param color - The background color (ComputedRef or string)
 * @returns Style object with backgroundColor or empty object
 */
export const createBackgroundStyle = (
  color: ComputedRef<string | undefined | null> | string | undefined | null
): Record<string, string> => {
  const colorValue = typeof color === 'string' ? color : color?.value;
  return colorValue ? { backgroundColor: colorValue } : {};
};

/**
 * Creates a style object with color if color is provided
 * @param color - The text color (ComputedRef or string)
 * @returns Style object with color or empty object
 */
export const createTextStyle = (
  color: ComputedRef<string | undefined> | string | undefined
): Record<string, string> => {
  const colorValue = typeof color === 'string' ? color : color?.value;
  return colorValue ? { color: colorValue } : {};
};

/**
 * Creates a style object with border if borderColor is provided
 * @param borderColor - The border color (ComputedRef or string | null | undefined)
 * @param borderWidth - Optional border width (ComputedRef or string | undefined). Defaults to '1px'
 * @param borderStyle - Optional border style (ComputedRef or string | undefined). Defaults to 'solid'
 * @returns Style object with border or empty object
 */
export const createBorderStyle = (
  borderColor: ComputedRef<string | null> | ComputedRef<string | undefined> | string | null | undefined,
  borderWidth?: ComputedRef<string | undefined> | string | undefined,
  borderStyle?: ComputedRef<string | undefined> | string | undefined
): Record<string, string> => {
  const borderColorValue = typeof borderColor === 'string' || borderColor === null ? borderColor : borderColor?.value;

  if (!borderColorValue) {
    return {};
  }

  const widthValue = borderWidth ? (typeof borderWidth === 'string' ? borderWidth : borderWidth?.value) : '1px';

  const styleValue = borderStyle ? (typeof borderStyle === 'string' ? borderStyle : borderStyle?.value) : 'solid';

  // Only create border if we have valid width and style
  if (!widthValue || !styleValue) {
    return {};
  }

  return { border: `${widthValue} ${styleValue} ${borderColorValue}` };
};

/**
 * Combines multiple style objects into one
 * @param styles - Array of style objects
 * @returns Combined style object
 */
export const combineStyles = (...styles: Array<Record<string, string>>): Record<string, string> => {
  return Object.assign({}, ...styles);
};
